/**
 * Qué se ve en el dashboard: el catálogo de TARJETAS KPI y el de ÁREAS
 * (gráficos, tablas y paneles). El usuario elige de ambas listas en el mismo
 * selector, y la elección viaja en la URL (`lib/filters.ts`).
 *
 * El orden de cada arreglo es el orden en que se pinta: la selección se
 * normaliza a él, así que dos vistas con los mismos elementos se ven idénticas.
 * Añadir algo al dashboard = añadir su id aquí y envolverlo en el `ver(id)` de
 * Dashboard.tsx; si no está en el catálogo, no se puede ocultar.
 */

/** Ordena una selección al orden del catálogo y quita repetidos. */
function enOrden<T extends string>(catalogo: readonly T[], elegidos: readonly T[]): T[] {
  const puestos = new Set<T>(elegidos);
  return catalogo.filter((id) => puestos.has(id));
}

// --- Tarjetas KPI ----------------------------------------------------------
// La etiqueta de aquí es la del SELECTOR (sin unidad): la de la tarjeta la arma
// el dashboard porque depende de la moneda activa (Bs/USD).

export const KPI_CATALOG = [
  { id: 'occ', label: 'Ocupación' },
  { id: 'adr', label: 'ADR' },
  { id: 'revpar', label: 'RevPAR' },
  { id: 'trevpar', label: 'TRevPAR' },
  { id: 'ingresoTotal', label: 'Ingreso total' },
  { id: 'ingresoHab', label: 'Ingreso habitaciones' },
  { id: 'diamond', label: 'Noches Diamond' },
  { id: 'diamondShare', label: '% Diamond' },
  { id: 'tipoLider', label: 'Tipo de habitación líder' },
] as const;

export type KpiId = (typeof KPI_CATALOG)[number]['id'];
export const KPI_IDS: KpiId[] = KPI_CATALOG.map((k) => k.id);

export function isKpiId(x: string): x is KpiId {
  return (KPI_IDS as string[]).includes(x);
}

export function sortKpis(ids: readonly KpiId[]): KpiId[] {
  return enOrden(KPI_IDS, ids);
}

// --- Áreas (gráficos, tablas y paneles) ------------------------------------
// Ocultar un área también le ahorra su llamada al API: los paneles que traen su
// propio dato (canales, cuentas, insights) no se montan, así que no consultan.

export const SECCION_CATALOG = [
  { id: 'ocupacionDia', label: 'Ocupación del día por hotel' },
  { id: 'ocupacion', label: 'Tendencia de ocupación' },
  { id: 'diamondMix', label: 'Diamond / House / Comp' },
  { id: 'diamondMensual', label: 'Noches Diamond por mes' },
  { id: 'mixIngreso', label: 'Mix de canal (ingreso)' },
  { id: 'ytd', label: 'Ingreso acumulado vs año previo' },
  { id: 'rankingSedes', label: 'Ranking de sedes' },
  { id: 'adrRevpar', label: 'ADR y RevPAR' },
  { id: 'ingresoYoY', label: 'Ingreso vs año previo' },
  { id: 'heatmap', label: 'Heatmap de ocupación' },
  { id: 'proyeccion', label: 'Proyección de cierre' },
  { id: 'anomalias', label: 'Anomalías' },
  { id: 'totalizacion', label: 'Totalización mensual' },
  { id: 'habitaciones', label: 'Tipos de habitación' },
  { id: 'markets', label: 'Rate codes (Res. Statistics 2)' },
  { id: 'operacion', label: 'Movimiento y pace' },
  { id: 'canales', label: 'Canales de venta' },
  { id: 'cuentas', label: 'Cuentas corporativas' },
  { id: 'insights', label: 'Insights' },
] as const;

export type SeccionId = (typeof SECCION_CATALOG)[number]['id'];
export const SECCION_IDS: SeccionId[] = SECCION_CATALOG.map((s) => s.id);

export function isSeccionId(x: string): x is SeccionId {
  return (SECCION_IDS as string[]).includes(x);
}

export function sortSecciones(ids: readonly SeccionId[]): SeccionId[] {
  return enOrden(SECCION_IDS, ids);
}
